import React from 'react';
import { Drawer, IconButton } from '@parsina/framework';
import { PanelRightClose, PanelRightOpen, Sparkles } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import VisitHeader from './VisitHeader.jsx';
import RecordRail from './rail/RecordRail.jsx';
import WorkPanel, { WorkTabs } from './work/WorkPanel.jsx';
import CopilotPanel from './copilot/CopilotPanel.jsx';

// The desktop workspace. Three columns at ≥1280px, start → end in RTL:
//
//   پرونده 300px (collapsible to a 40px strip, remembered in
//   `localStorage.visit_rail_v1`) · کار flex · دستیار 360px (collapsible)
//
// Between 769 and 1279px the copilot becomes a right-edge `Drawer` rather than a
// third column — core.md §7.5, and the reason is arithmetic: at 1100px three
// columns leave the note about 340px wide, which is a phone's column with a
// desktop's line height.
//
// ── The rail collapses to a STRIP, not to nothing ────────────────────────
//
// 40px of vertical label, still clickable. A rail that vanishes is a rail whose
// re-opening control the student has to hunt for in a header they are not
// looking at, and «where did the allergies go» is not a question a clinical
// screen should ever raise.

const RAIL_WIDTH = 300;
const COPILOT_WIDTH = 360;
const STRIP_WIDTH = 40;

export default function VisitWeb({ vm }) {
  const { t } = useTranslation();
  const showCopilotColumn = !vm.isNarrowDesktop && vm.copilotOpen;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      data-testid="visit-web"
    >
      <VisitHeader
        encounter={vm.encounter}
        patient={vm.patient}
        saveState={vm.saveState}
        onRetrySave={vm.retrySave}
        onBack={vm.onBack}
        onEnd={vm.onEnd}
        onReopen={vm.onReopen}
        onCancel={vm.onCancel}
        readOnly={vm.readOnly}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 12, padding: 12 }}>
        {/* The record rail. */}
        <aside
          style={{
            width: vm.railCollapsed ? STRIP_WIDTH : RAIL_WIDTH,
            flexShrink: 0,
            overflowY: 'auto',
            transition: 'width .15s',
          }}
          data-testid="visit-rail-column"
          data-collapsed={vm.railCollapsed ? 'true' : 'false'}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
            <IconButton
              size={32}
              variant="ghost"
              icon={vm.railCollapsed
                ? <PanelRightOpen size={16} aria-hidden />
                : <PanelRightClose size={16} aria-hidden />}
              label={t(vm.railCollapsed ? 'visit.rail.expand' : 'visit.rail.collapse')}
              onClick={vm.toggleRail}
              data-testid="visit-rail-toggle"
            />
          </div>
          {vm.railCollapsed ? (
            <button
              type="button"
              onClick={vm.toggleRail}
              style={{
                writingMode: 'vertical-rl', border: 0, background: 'none', font: 'inherit',
                cursor: 'pointer', fontSize: 12, fontWeight: 700, color: CT.mute, padding: '8px 0',
              }}
              data-testid="visit-rail-strip"
            >
              {t('visit.rail.title')}
            </button>
          ) : (
            <RecordRail record={vm.record} onAddVitals={vm.addVitals} readOnly={vm.readOnly} />
          )}
        </aside>

        {/* The work column. */}
        <main
          style={{
            flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          data-testid="visit-work-column"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <WorkTabs value={vm.workTab} onChange={vm.setWorkTab} />
            {vm.isNarrowDesktop && (
              <IconButton
                size={32}
                variant="ghost"
                icon={<Sparkles size={16} aria-hidden />}
                label={t('visit.copilot.open')}
                onClick={() => vm.setCopilotOpen(true)}
                style={{ marginInlineStart: 'auto' }}
                data-testid="visit-copilot-open"
              />
            )}
          </div>
          <WorkPanel vm={vm} tab={vm.workTab} />
        </main>

        {/* The copilot column. */}
        {showCopilotColumn && (
          <aside
            style={{
              width: COPILOT_WIDTH, flexShrink: 0, minHeight: 0,
              display: 'flex', flexDirection: 'column',
              borderInlineStart: `1px solid ${C.border}`, paddingInlineStart: 12,
            }}
            data-testid="visit-copilot-column"
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <IconButton
                size={32}
                variant="ghost"
                icon={<PanelRightClose size={16} aria-hidden />}
                label={t('visit.copilot.collapse')}
                onClick={() => vm.setCopilotOpen(false)}
                data-testid="visit-copilot-collapse"
              />
            </div>
            <CopilotPanel vm={vm} tab={vm.copilotTab} onTab={vm.setCopilotTab} />
          </aside>
        )}

        {!vm.isNarrowDesktop && !vm.copilotOpen && (
          <IconButton
            size={36}
            variant="outline"
            icon={<Sparkles size={16} aria-hidden />}
            label={t('visit.copilot.open')}
            onClick={() => vm.setCopilotOpen(true)}
            data-testid="visit-copilot-reopen"
          />
        )}
      </div>

      {/* 769–1279px: the copilot as a right-edge drawer. */}
      {vm.isNarrowDesktop && (
        <Drawer open={vm.copilotOpen} onClose={() => vm.setCopilotOpen(false)} width={COPILOT_WIDTH}>
          <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 12, minHeight: 0 }}
            data-testid="visit-copilot-drawer"
          >
            <CopilotPanel vm={vm} tab={vm.copilotTab} onTab={vm.setCopilotTab} />
          </div>
        </Drawer>
      )}
    </div>
  );
}
